import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Check, Star, BookOpen, LayoutGrid, Calendar, Award, Smartphone, Cloud, Share2, Zap, HeartHandshake, ChevronLeft, ChevronRight } from "lucide-react";
import { PRODUCTS, PLANNER_PAGES, REVIEWS, IMAGES, formatINR } from "@/lib/products";
import { useCart } from "@/context/CartContext";
import { PlannerPagePreview } from "@/components/product/PlannerPagePreview";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { subscribeNewsletter } from "@/lib/newsletter";

const bestseller = PRODUCTS.find((p) => p.id === "life-os-planner");

const TRUST = [
  { icon: BookOpen, label: "200+ Premium Structured Pages" },
  { icon: LayoutGrid, label: "6+ Core Sections" },
  { icon: Calendar, label: "Undated Planner" },
  { icon: Award, label: "Premium Quality" },
];

const INSTAGRAM_IMAGES = [
  "/instagram/planner-cover-hand.jpeg",
  "/instagram/daily-dashboard.jpeg",
  "/instagram/weekly-mba-planner.jpeg",
  "/instagram/weekly-review.jpeg",
  "/instagram/thank-you-note.jpeg",
  "/instagram/planner-cover-close.jpeg",
];

const INSTAGRAM_URL = "https://www.instagram.com/note.console?igsh=MTd2MW9hcWh6eG93OQ==";

const BESTSELLER_IMAGES = [
  "/product/life-os-cocoa-desk.jpg",
  "/product/life-os-cocoa-standing.jpg",
  ...PLANNER_PAGES.map((page) => page.img),
];

const PRODUCT_PATH = "/product/life-os-planner";

export default function Home() {
  const { addItem } = useCart();
  const navigate = useNavigate();
  const bestsellerRef = useRef(null);
  const [bestsellerImage, setBestsellerImage] = useState(0);
  const [bestsellerInView, setBestsellerInView] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState("");

  useEffect(() => {
    const node = bestsellerRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => setBestsellerInView(entry.isIntersecting),
      { threshold: 0.35 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!bestsellerInView) return undefined;

    const timer = window.setInterval(() => {
      setBestsellerImage((current) => (current + 1) % BESTSELLER_IMAGES.length);
    }, 2200);

    return () => window.clearInterval(timer);
  }, [bestsellerInView]);

  const showPreviousBestsellerImage = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setBestsellerImage((current) =>
      current === 0 ? BESTSELLER_IMAGES.length - 1 : current - 1
    );
  };

  const showNextBestsellerImage = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setBestsellerImage((current) => (current + 1) % BESTSELLER_IMAGES.length);
  };

  const submitNewsletter = async (event) => {
    event.preventDefault();
    if (!newsletterEmail.trim()) return toast.error("Please enter your email");
    try {
      await subscribeNewsletter(newsletterEmail, "home");
      toast.success("You're subscribed to the newsletter.");
      setNewsletterEmail("");
    } catch (error) {
      console.error(error);
      toast.error("Could not subscribe. Please try again.");
    }
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="container-nc grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center pt-10 pb-20 lg:pt-16 lg:pb-28">
          <div className="animate-fade-up">
            <p className="overline mb-6">Not Just a Planner.</p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] text-espresso">
              It&rsquo;s Your
              <br />
              <span className="italic">Life OS.</span>
            </h1>
            <p className="mt-8 text-lg text-muted_brown max-w-md leading-relaxed">
              A premium planner for the driven. Designed for clarity. Built for success.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/product/life-os-planner" data-testid="hero-shop-now" className="btn-primary">
                Shop Now <ArrowRight size={16} />
              </Link>
              <Link to="/inside-the-planner" data-testid="hero-view-pages" className="btn-outline">
                View Pages
              </Link>
            </div>
            <div className="mt-20 md:mt-24 font-serif text-3xl md:text-5xl lg:text-[3.5rem] text-espresso leading-[1.1] font-bold">
              <div>From thought to plan.</div>
              <div className="mt-3 md:mt-4">From plan to progress.</div>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-6 bg-champagne/30 rounded-[2rem] -rotate-2" aria-hidden />
            <img
              src="/product/life-os-planner-desk.jpg"
              alt="Life OS Planner on a desk"
              className="relative rounded-2xl w-full aspect-[4/5] object-cover shadow-medium"
              fetchPriority="high"
            />
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-line bg-ivory/60">
        <div className="container-nc grid grid-cols-2 md:grid-cols-4 gap-6 py-10">
          {TRUST.map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center gold-border">
                <Icon size={16} strokeWidth={1.5} className="text-espresso" />
              </div>
              <div className="text-sm text-espresso font-medium">{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* EMOTIONAL — Why plan */}
      <section className="container-nc py-24 lg:py-32">
        <div className="max-w-4xl mx-auto text-center">
          <p className="overline mb-6">A quiet manifesto</p>
          <h2 className="font-serif text-4xl md:text-6xl text-espresso leading-[1.05]">
            Some days you rise <span className="italic">on fire.</span>
            <br />
            Other days you barely rise at all.
          </h2>
          <div className="mt-10 space-y-5 text-muted_brown text-lg leading-relaxed max-w-2xl mx-auto">
            <p>
              Note Console isn&rsquo;t here to make you productive. It&rsquo;s here to help you
              come home to yourself — one page, one morning, one honest week at a time.
            </p>
            <p>
              A place to write down the goals you don&rsquo;t say out loud. To track the habit
              you keep starting over. To remember what mattered on the days you almost forgot.
            </p>
          </div>
          <div className="mt-12 inline-flex items-center gap-3 text-espresso border-b border-gold pb-2 text-xs uppercase tracking-[0.28em]">
            <span>Made for the ones who show up anyway</span>
          </div>
        </div>
      </section>

      {/* DIGITAL COMPANION */}
      <section className="bg-espresso text-cream">
        <div className="container-nc py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className="overline text-champagne mb-5">The Note Console Companion</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">
              Your planner. <span className="italic text-gold">On every device.</span>
            </h2>
            <p className="mt-6 text-cream/80 leading-relaxed max-w-lg">
              Paper is where the day begins. But your goals shouldn&rsquo;t stay stuck to your
              desk. Every Note Console planner ships with a free, private digital companion
              — accessible from your phone, tablet, and laptop.
            </p>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { i: Smartphone, t: "iOS &amp; Android", d: "Snap a page, sync to your library" },
                { i: Cloud, t: "Cloud-backed", d: "Never lose a reflection again" },
                { i: Share2, t: "Share Everywhere", d: "Export and share your plans with ease" },
                { i: Zap, t: "Instant search", d: "Find anything you&rsquo;ve ever written" },
              ].map(({ i: Icon, t, d }) => (
                <li key={t} className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full gold-border flex items-center justify-center shrink-0">
                    <Icon size={14} strokeWidth={1.5} className="text-gold" />
                  </div>
                  <div>
                    <div className="text-sm font-medium" dangerouslySetInnerHTML={{ __html: t }} />
                    <div className="text-xs text-cream/70 mt-0.5" dangerouslySetInnerHTML={{ __html: d }} />
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/product/digital-planner-pack" className="btn-primary bg-gold text-espresso hover:bg-champagne">
                Preview the companion <ArrowRight size={16} />
              </Link>
              <Link to="/inside-the-planner" className="text-sm uppercase tracking-[0.2em] border-b border-gold pb-1 text-cream hover:text-gold">
                See how it works
              </Link>
            </div>
            <p className="mt-8 text-sm md:text-base text-cream/75 font-medium">
              Included free with every planner as Complimentary*
            </p>
          </div>

          {/* Mock device cluster */}
          <div className="relative aspect-square flex items-center justify-center">
            <img
              src={IMAGES.planner_open}
              alt="Planner"
              className="absolute w-3/4 aspect-[4/5] object-cover rounded-2xl shadow-hover -rotate-3 -translate-x-4"
              loading="lazy"
              decoding="async"
            />
            <div className="absolute right-0 top-8 w-36 aspect-[9/19] rounded-[2.4rem] bg-cream border-4 border-espresso-dark p-1.5 shadow-hover rotate-6 overflow-hidden">
              <div className="w-full h-full rounded-[1.8rem] bg-ivory overflow-hidden relative">
                <div className="absolute inset-0 bg-cream" />
                <div className="absolute top-3 left-3 right-3 text-[8px] uppercase tracking-[0.15em] text-muted_brown">Wednesday</div>
                <div className="absolute top-9 left-3 right-3 font-serif text-[13px] text-espresso leading-tight">
                  Notes &amp;<br />reflections
                </div>
                <div className="absolute bottom-4 left-3 right-3 space-y-1.5">
                  {["Morning pages", "Habit — Read", "Weekly review"].map((t, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-gold" />
                      <div className="text-[8px] text-espresso">{t}</div>
                    </div>
                  ))}
                </div>
                <div className="absolute inset-x-0 bottom-1.5 flex justify-center">
                  <div className="w-8 h-0.5 bg-espresso rounded-full" />
                </div>
              </div>
            </div>
            <div className="absolute left-0 bottom-6 w-56 aspect-[16/11] rounded-xl bg-cream border-4 border-espresso-dark p-2 shadow-hover -rotate-6">
              <div className="w-full h-full rounded-md bg-ivory relative overflow-hidden">
                <div className="absolute top-2 left-2 text-[8px] text-muted_brown">note-console/dashboard</div>
                <div className="absolute top-6 left-3 right-3 grid grid-cols-3 gap-1">
                  {["Pages", "Streak", "Goals"].map((k) => (
                    <div key={k} className="rounded-sm bg-champagne/60 p-1">
                      <div className="text-[6px] text-muted_brown uppercase">{k}</div>
                      <div className="text-[11px] font-serif text-espresso mt-0.5">
                        {k === "Pages" ? "82" : k === "Streak" ? "14d" : "5"}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="absolute bottom-2 left-3 right-3 space-y-1">
                  {[80, 60, 45].map((w, i) => (
                    <div key={i} className="h-1 rounded-full bg-champagne/60 overflow-hidden">
                      <div className="h-full bg-gold" style={{ width: `${w}%` }} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INSIDE THE PLANNER */}
      <section className="container-nc py-20 lg:py-28">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="overline mb-4">Inside the planner</p>
            <h2 className="font-serif text-4xl md:text-5xl text-espresso">What&rsquo;s Inside?</h2>
          </div>
          <Link to="/inside-the-planner" className="btn-ghost-gold hidden md:inline-flex">
            Explore All Pages <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {PLANNER_PAGES.slice(0, 12).map((p) => (
            <Link
              key={p.key}
              to="/inside-the-planner"
              data-testid={`inside-card-${p.key}`}
              className="group"
            >
              <div className="aspect-[3/4] rounded-lg overflow-hidden bg-ivory border border-line">
                <PlannerPagePreview
                  page={p}
                  className="transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="mt-3 text-center">
                <div className="text-sm text-espresso font-medium">{p.label}</div>
              </div>
            </Link>
          ))}
        </div>
        <p className="mt-10 text-center font-serif text-2xl md:text-3xl text-espresso italic">
          and many more sections ...
        </p>
      </section>

      {/* BESTSELLER FEATURE */}
      <section ref={bestsellerRef} className="bg-ivory">
        <div className="container-nc py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <div
              role="link"
              tabIndex={0}
              onClick={() => navigate(PRODUCT_PATH)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") navigate(PRODUCT_PATH);
              }}
              className="block w-full cursor-pointer"
            >
              <div className="relative rounded-2xl w-full aspect-square overflow-hidden shadow-soft bg-cream">
              {BESTSELLER_IMAGES.map((src, index) => (
                <img
                  key={src}
                  src={src}
                  alt="Life OS Planner"
                  className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${
                    index === bestsellerImage ? "opacity-100" : "opacity-0"
                  }`}
                  loading={index === 0 ? "eager" : "lazy"}
                  fetchPriority={index === 0 ? "high" : "auto"}
                  decoding="async"
                />
              ))}
              <button
                type="button"
                  onClick={showPreviousBestsellerImage}
                aria-label="Previous planner image"
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-cream/90 text-espresso border border-line flex items-center justify-center shadow-soft hover:bg-champagne transition-colors"
              >
                <ChevronLeft size={18} strokeWidth={1.7} />
              </button>
              <button
                type="button"
                  onClick={showNextBestsellerImage}
                aria-label="Next planner image"
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-cream/90 text-espresso border border-line flex items-center justify-center shadow-soft hover:bg-champagne transition-colors"
              >
                <ChevronRight size={18} strokeWidth={1.7} />
              </button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-cream/90 px-3 py-1 text-xs text-espresso shadow-soft">
                {bestsellerImage + 1} / {BESTSELLER_IMAGES.length}
              </div>
              </div>
            </div>
            <div className="mt-4 flex gap-2 overflow-x-auto pb-2">
              {BESTSELLER_IMAGES.map((src, index) => (
                <button
                  key={src}
                  type="button"
                  onClick={() => setBestsellerImage(index)}
                  aria-label={`Show planner image ${index + 1}`}
                  className={`h-16 w-16 md:h-20 md:w-20 shrink-0 overflow-hidden rounded-lg border transition-all ${
                    index === bestsellerImage ? "border-gold ring-2 ring-gold/30" : "border-line opacity-70 hover:opacity-100"
                  }`}
                >
                  <img src={src} alt="" className="h-full w-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          </div>
          <div
            role="link"
            tabIndex={0}
            onClick={() => navigate(PRODUCT_PATH)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") navigate(PRODUCT_PATH);
            }}
            className="cursor-pointer"
          >
            <p className="overline mb-4">BESTSELLER - 4.9 rating - 167 rating</p>
            <h2 className="font-serif text-4xl md:text-5xl text-espresso leading-tight">
              The Life OS Planner
            </h2>
            <p className="mt-3 text-xs uppercase tracking-[0.2em] text-muted_brown">
              B5 - Hard Binding showcase
            </p>
            <p className="mt-6 text-muted_brown text-lg leading-relaxed max-w-lg">
              {bestseller.description}
            </p>
            <ul className="mt-8 space-y-3">
              {bestseller.bullets.slice(0,4).map((b) => (
                <li key={b} className="flex items-start gap-3 text-espresso">
                  <Check size={16} className="text-gold mt-0.5" />
                  <span className="text-sm">{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10 flex items-center gap-6">
              <div className="text-3xl font-serif text-espresso">
                Starting {formatINR(399)}
              </div>
              <Link to={PRODUCT_PATH} className="btn-outline" onClick={(event) => event.stopPropagation()}>
                View Details
              </Link>
              <button
                data-testid="bestseller-add"
                onClick={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  addItem(bestseller);
                }}
                className="btn-primary"
              >
                Add to Cart
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* WHY IT WORKS */}
      <section className="container-nc py-20 lg:py-28">
        <div className="max-w-2xl">
          <p className="overline mb-4">Why it works</p>
          <h2 className="font-serif text-4xl md:text-5xl text-espresso leading-tight">
            Clarity is the new productivity.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-14">
          {[
            { t: "Plan with Purpose", d: "Every page is designed to help you set intentions, stay focused, and follow through." },
            { t: "See What Matters", d: "Simplify the noise. Focus on what truly moves the needle in your day." },
            { t: "Grow Every Day", d: "Small consistent actions lead to big transformation. We're here to support that." },
          ].map((c) => (
            <div key={c.t} className="card-nc p-8">
              <div className="w-10 h-10 rounded-full flex items-center justify-center gold-border mb-6">
                <Star size={14} className="text-gold" strokeWidth={0} fill="currentColor" />
              </div>
              <h3 className="font-serif text-2xl text-espresso mb-3 font-bold">
                <span className="bg-champagne/70 px-1.5 py-0.5 box-decoration-clone">
                  {c.t}
                </span>
              </h3>
              <p className="text-sm text-muted_brown leading-relaxed">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* LIFESTYLE STRIP */}
      <section className="relative h-[400px] md:h-[520px] overflow-hidden">
        <img
          src={IMAGES.desk_scene}
          alt="Lifestyle desk scene"
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
          decoding="async"
        />
        <img
          src="/NC_LOGO.png"
          alt="Note Console logo"
          className="absolute top-[12%] left-[49%] w-20 md:w-28 lg:w-36 opacity-80 mix-blend-multiply"
        />
        <div className="absolute inset-0 bg-espresso/30" />
        <div className="relative container-nc h-full flex flex-col justify-end pb-16">
          <p className="text-cream/80 text-sm tracking-[0.2em] uppercase mb-4">Designed for the driven</p>
          <h2 className="font-serif text-4xl md:text-6xl text-cream max-w-2xl leading-tight">
            A tool built to last as long as your ambition.
          </h2>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="container-nc py-20 lg:py-28">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="overline mb-4">Loved by Users</p>
          <div className="flex items-center justify-center gap-2">
            {[...Array(5)].map((_,i) => <Star key={i} size={18} className="fill-gold text-gold" strokeWidth={0}/>)}
            <span className="ml-2 text-espresso font-medium">4.9 / 5</span>
          </div>
          <h2 className="font-serif text-4xl md:text-5xl text-espresso mt-6">
            From our community.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {REVIEWS.map((r,i) => (
            <div key={i} className="card-nc p-7">
              <div className="flex gap-0.5 mb-4">
                {[...Array(r.rating)].map((_,j) => <Star key={j} size={12} className="fill-gold text-gold" strokeWidth={0}/>)}
              </div>
              <h4 className="font-serif text-lg text-espresso mb-3">{r.title}</h4>
              <p className="text-sm text-muted_brown leading-relaxed">"{r.body}"</p>
              <div className="mt-5 pt-4 border-t border-line">
                <div className="text-sm text-espresso font-medium">{r.name}</div>
                <div className="text-xs text-muted_brown">{r.location}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* STATS */}
      <section className="bg-ivory border-y border-line">
        <div className="container-nc py-16 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { n: "Loved By", l: "Users" },
            { n: "4.9/5", l: "Average rating" },
            { n: "Quality", l: "Premium pages" },
            { n: "6+", l: "Core sections" },
          ].map((s) => (
            <div key={s.l}>
              <div className="font-serif text-4xl md:text-5xl text-espresso">{s.n}</div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted_brown mt-2">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* BUNDLE FEATURE */}
      <section className="container-nc py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div>
          <p className="overline mb-4">Featured bundle</p>
          <h2 className="font-serif text-4xl md:text-5xl text-espresso leading-tight">
            The Complete Kit.
          </h2>
          <p className="mt-6 text-muted_brown text-lg leading-relaxed max-w-md">
            Includes the Life OS Planner, Habit Tracker Planner, and Digital Companion
            with extended months - everything you need to plan, track, and stay consistent.
          </p>
          <Link to="/product/productivity-bundle" className="btn-primary mt-8">
            Shop the Bundle <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <img src={IMAGES.planner_closed} alt="Planner" className="rounded-xl aspect-square object-cover row-span-2 h-full" loading="lazy" decoding="async"/>
          <img src={IMAGES.gold_pen} alt="Gold Pen" className="rounded-xl aspect-square object-cover" loading="lazy" decoding="async"/>
          <img src={IMAGES.stickers} alt="Stickers" className="rounded-xl aspect-square object-cover" loading="lazy" decoding="async"/>
        </div>
      </section>

      {/* INSTAGRAM GALLERY */}
      <section className="container-nc pb-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="overline mb-3 inline-block hover:text-gold">
              @note.console
            </a>
            <h2 className="font-serif text-3xl md:text-4xl text-espresso">On Instagram</h2>
          </div>
          <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="btn-ghost-gold hidden md:inline-flex">
            Follow Us <ArrowRight size={14} />
          </a>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {INSTAGRAM_IMAGES.map((src,i) => (
            <a
              key={i}
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noreferrer"
              className="aspect-square rounded-lg overflow-hidden bg-ivory"
            >
              <img src={src} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy"/>
            </a>
          ))}
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="bg-champagne/60">
        <div className="container-nc py-20 text-center max-w-2xl mx-auto">
          <p className="overline mb-4">Join the list</p>
          <h2 className="font-serif text-4xl md:text-5xl text-espresso">
            Planning tips, straight to your inbox.
          </h2>
          <p className="text-muted_brown mt-4">
            Get thoughtful essays on intentional living and first access to new releases.
          </p>
          <form
            onSubmit={submitNewsletter}
            className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
          >
            <input
              type="email"
              data-testid="home-newsletter-input"
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)}
              placeholder="Enter your email"
              className="input-nc flex-1 bg-cream"
            />
            <button data-testid="home-newsletter-submit" className="btn-primary">Subscribe</button>
          </form>
        </div>
      </section>
    </div>
  );
}
